import { Button } from 'component/Button'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { Icon } from 'icon/Icon'
import { observer } from 'mobx-react-lite'
import { DashedButton } from 'page/ProfileScreen/basic/DashedButton'
import { ErrorPopover } from 'page/ProfileScreen/ErrorPopover/ErrorPopover'
import { PhotoHelpStep } from 'page/ProfileScreen/HelpStep/PhotoHelpStep'
import { CameraInput } from 'page/ProfileScreen/Selfies/CameraInput'
import { ChangeEvent, useRef } from 'react'
import { controller } from 'store/controller'
import { store } from 'store/store'
import css from './FullStep.module.scss'

type Props = {
  countdown: number
}

export const FullStep = observer(({ countdown }: Props) => {
  const { userPhoto, camera } = store.profile.common
  const photo = userPhoto
  const done = !!photo.url
  const file = useRef<HTMLInputElement>(null)

  const onHow = useLatestCallback(() => {
    store.profile.step = 'help'
  })

  const onFile = useLatestCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) await photo.setFile(file)
  })

  const onImage = useLatestCallback(() => {
    file.current?.click()
  })

  const onTake = useLatestCallback(async () => {
    await camera.countdown(countdown)
    await store.profile.common.take(true)
  })

  const onFlip = useLatestCallback(async () => {
    await camera.switchCamera()
  })

  const onRepeat = useLatestCallback(() => {
    photo.setUrl(undefined)
  })

  const onContinue = useLatestCallback(async () => {
    await controller.saveProfile()
  })

  return <Vertical wide tall spring gap={16}>
    <Vertical wide tall spring gap={16} stable center>
      <Space className={css.photo}>
        <CameraInput simple square selfie={photo} />
        <ErrorPopover selfie={photo} />
      </Space>

      {!done && <Vertical gap={12} wide center>
        <Typo size={12} center upper pre>
          {i18n('NowTakeFullBodyPhoto')}
        </Typo>
        <DashedButton onClick={onHow}>
          {i18n('HowToMake')}
        </DashedButton>
      </Vertical>}

      {done && <Space center>
        <Typo size={12} center upper pre>
          {i18n('Done')}
        </Typo>
      </Space>}

      <input ref={file} style={{ display: 'none' }} type="file" accept="image/*" onChange={onFile} />

      {store.profile.step === 'help' && <PhotoHelpStep />}
    </Vertical>

    {done && <Space gap={16} wide stable columns>
      <Button gap={8} pad={0} size={48} flat action={onRepeat}>
        <Icon size={16} name="autorenew" />
        <Typo size={13} medium upper>
          {i18n('Repeat')}
        </Typo>
      </Button>
      <Button gap={8} pad={0} size={48} primary action={onContinue}>
        <Typo size={13} medium upper>
          {i18n('Continue')}
        </Typo>
        <Icon size={16} name="chevron_right" />
      </Button>
    </Space>}

    {!done && <Space gap={16} pad={[0, 32]} opposite stable>
      <Button size={48} flat round action={onImage}>
        <Icon size={16} name="image" />
      </Button>
      <Button size={64} primary round action={onTake}>
        <Icon size={16} name="photo_camera" fill />
      </Button>
      <Button size={48} flat round action={onFlip}>
        <Icon size={16} name="autorenew" />
      </Button>
    </Space>}
  </Vertical>
})
